import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Clock, CreditCard, X, ChevronRight, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const RetentionBanners = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissedInactivity, setDismissedInactivity] = useState(false);
  const [dismissedExpiry, setDismissedExpiry] = useState(false);

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      // Not logged in or error
    }
  };

  if (!user || !status) return null;

  const showInactivityBanner = status.days_inactive >= 4 && !dismissedInactivity;
  const showPixExpiry = status.last_payment_method === 'pix' 
    && status.days_until_expiry !== null 
    && status.days_until_expiry <= 3 
    && status.days_until_expiry > 0
    && !dismissedExpiry;

  return (
    <>
      {/* 1. Banner de Inatividade - amarelo no topo */}
      <AnimatePresence>
        {showInactivityBanner && (
          <motion.div
            className="fixed top-0 left-0 right-0 z-[52] bg-amber-500/95 backdrop-blur-sm"
            initial={{ y: -60 }}
            animate={{ y: 0 }}
            exit={{ y: -60 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            data-testid="inactivity-banner"
          >
            <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-black flex-shrink-0" />
              <p className="text-black text-xs sm:text-sm font-medium flex-1">
                Voce esta ha {status.days_inactive} dias sem anotar seus gastos. A IA precisa desses dados para recalcular sua rota financeira hoje!
              </p>
              <Button
                size="sm"
                className="bg-black text-amber-400 hover:bg-black/80 text-xs rounded-full px-3 flex-shrink-0"
                onClick={() => navigate('/expenses')}
                data-testid="inactivity-banner-cta"
              >
                Anotar agora
              </Button>
              <button
                onClick={() => setDismissedInactivity(true)}
                className="text-black/60 hover:text-black transition-colors flex-shrink-0"
                data-testid="inactivity-banner-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. Alerta de Vencimento Pix - rodapé sutil */}
      <AnimatePresence>
        {showPixExpiry && (
          <motion.div
            className="fixed bottom-[76px] left-3 right-3 z-[58] bg-[#0A0A0A] border border-amber-400/30 rounded-xl shadow-[0_0_20px_rgba(0,0,0,0.8)]"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            data-testid="pix-expiry-banner"
          >
            <div className="px-4 py-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs sm:text-sm font-medium">
                  Seu acesso ao organizador e relatorios vence em {status.days_until_expiry} dia{status.days_until_expiry !== 1 ? 's' : ''}.
                </p>
                <button 
                  onClick={() => {
                    const pkg = status.subscription_type === 'premium' ? 'premium_monthly' : 'basic_monthly';
                    navigate(`/pix?package=${pkg}`);
                  }}
                  className="text-amber-400 text-xs underline hover:text-amber-300 transition-colors mt-0.5"
                  data-testid="pix-expiry-cta"
                >
                  Clique aqui para gerar seu novo Pix e manter seus dados salvos
                  <ChevronRight className="w-3 h-3 inline ml-0.5" />
                </button>
              </div>
              <button
                onClick={() => setDismissedExpiry(true)}
                className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                data-testid="pix-expiry-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RetentionBanners;
